import { API_CONFIG, SESSION_KEYS } from './config';

/** Flag to prevent multiple concurrent refresh attempts */
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

/** Flag to prevent multiple simultaneous logout redirects */
let isLoggingOut = false;

/**
 * Attempt to refresh the access token using the stored refresh token.
 * Returns the new access token on success, or null on failure.
 */
async function tryRefreshToken(): Promise<string | null> {
  // Deduplicate concurrent refresh calls
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  const refreshToken =
    typeof window !== 'undefined'
      ? localStorage.getItem('hf-app:refresh-token')
      : null;

  if (!refreshToken) return null;

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const res = await fetch(
        `${API_CONFIG.baseUrl}${API_CONFIG.versionPrefix}/auth/token/refresh`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        },
      );

      if (!res.ok) return null;

      const data = await res.json();
      const newToken: string | undefined = data.accessToken;

      if (newToken && typeof window !== 'undefined') {
        localStorage.setItem('hf-app:access-token', newToken);
      }

      return newToken ?? null;
    } catch {
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export async function refreshAccessToken(): Promise<string | null> {
  return tryRefreshToken()
}

export class ApiClientError extends Error {
  constructor(
    public statusCode: number,
    public statusText: string,
    public body?: unknown,
  ) {
    // Extract the human-friendly message from the backend response body.
    // NestJS ValidationPipe returns { statusCode, message } (string | string[]),
    // but hand-thrown BadRequest/NotFound bodies key the reason as `error`
    // (+ an optional `issues[]` array with per-item `message`s — e.g. workflow
    // activation gates). Without reading those, a custom 400/404 falls back to
    // `API Error 400:`/`API Error 404:` (empty statusText) → a useless toast.
    const backendMessage = ApiClientError.extractMessage(body);
    super(backendMessage || `API Error ${statusCode}: ${statusText}`);
    this.name = 'ApiClientError';
  }

  /**
   * Pull the best human-readable reason out of a NestJS error body, in order:
   *   1. `message` (ValidationPipe) — string | string[]
   *   2. `error`   (hand-thrown BadRequest/NotFound) — string
   *   3. `issues[].message` (activation/validation gates) — joined
   * Returns undefined when the body carries none of these.
   */
  private static extractMessage(body: unknown): string | undefined {
    if (!body || typeof body !== 'object') return undefined;
    const rec = body as Record<string, unknown>;

    if ('message' in rec) {
      const m = rec.message;
      if (Array.isArray(m)) {
        const joined = m.filter((x) => typeof x === 'string').join('. ');
        if (joined) return joined;
      } else if (typeof m === 'string' && m) {
        return m;
      }
    }

    // Collect issue lines up front so `error` can lead them (e.g.
    // "Fix 2 issues before turning this on: …") for the fullest toast.
    const issueLines = Array.isArray(rec.issues)
      ? rec.issues
          .map((i) =>
            i && typeof i === 'object' && typeof (i as Record<string, unknown>).message === 'string'
              ? ((i as Record<string, unknown>).message as string)
              : null,
          )
          .filter((x): x is string => !!x)
      : [];

    const headline = typeof rec.error === 'string' && rec.error ? rec.error : undefined;

    if (headline && issueLines.length) return `${headline}: ${issueLines.join('. ')}`;
    if (headline) return headline;
    if (issueLines.length) return issueLines.join('. ');
    return undefined;
  }
}

/**
 * Universal API client → the NestJS backend at API_CONFIG.baseUrl + /v1.
 *
 * skipAuth (portal hooks, OTP login, onboarding bootstrap) bypasses the
 * admin token + workspace header entirely.
 */
export async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit & { skipAuth?: boolean },
): Promise<T> {
  const url = buildUrl(endpoint);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string>),
  };

  if (!options?.skipAuth) {
    const token = getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const workspaceId = getWorkspaceId();
    if (workspaceId) {
      headers['x-workspace-id'] = workspaceId;
    }
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    // Auto-refresh and retry on 401 in production mode.
    //
    // skipAuth requests are explicit "don't touch admin auth" calls
    // (portal hooks, OTP login, onboarding bootstrap). Those carry
    // their own bearer (portal JWT) or run anonymously. Treating their
    // 401 as an admin-session-expired signal nukes the admin token,
    // wipes cookies, and force-redirects to /login — which booted the
    // employee out of /portal back to the admin login page.
    //
    // Behaviour on 401 + skipAuth: throw a clean ApiClientError(401)
    // so the caller can decide what to do (show toast, redirect to
    // /portal/login, etc). DO NOT mutate admin auth state.
    if (response.status === 401 && !options?.skipAuth) {
      // 401: try refresh, then force logout if that fails.
      const refreshed = await tryRefreshToken();
      if (refreshed) {
        // Retry the original request with the new token
        headers['Authorization'] = `Bearer ${refreshed}`;
        const retryResponse = await fetch(url, { ...options, headers });
        if (retryResponse.ok) {
          const ct = retryResponse.headers.get('content-type');
          if (!ct || !ct.includes('application/json')) {
            return undefined as T;
          }
          return retryResponse.json();
        }
      }
      // Refresh failed or retry failed — force logout (deduplicated).
      //
      // Loop guard (P0 fix 2026-05-06): if the user is ALREADY on
      // /login (or any /auth/* page), DO NOT redirect — that re-fires
      // the redirect every time the page mounts a query and 401s,
      // making the URL bar flash /login → /login → /login. Just throw
      // the error; the page itself can render a "session expired"
      // message and let the user re-login.
      // Routes where an admin-token 401 must NOT nuke admin auth + redirect to
      // /login. Besides the login surfaces, this covers the non-admin realms:
      // the employee portal (/portal) and the PhishTrainer player (/play). Those
      // run on a recipient JWT, so a stray admin-scoped 401 there (e.g. a shared
      // guard calling /me/modules) is expected and must be ignored — otherwise
      // the learner gets booted to the admin login page.
      const onLoginRoute =
        typeof window !== 'undefined' &&
        (window.location.pathname === '/login' ||
          window.location.pathname.startsWith('/auth/') ||
          window.location.pathname.startsWith('/portal') ||
          window.location.pathname.startsWith('/play') ||
          // Public invite-acceptance surface: it manages identity itself
          // (register / sign-out-and-continue / stale-token recovery). A stray
          // 401 here must surface to the page's own catch, not hijack the
          // invitee to /login mid-flow.
          window.location.pathname.startsWith('/accept-invite'));

      if (
        typeof window !== 'undefined' &&
        !isLoggingOut &&
        !onLoginRoute
      ) {
        isLoggingOut = true;
        localStorage.removeItem(SESSION_KEYS.REAL_TOKEN_KEY);
        localStorage.removeItem(SESSION_KEYS.REAL_REFRESH_KEY);
        localStorage.removeItem(SESSION_KEYS.REAL_WORKSPACE_KEY);
        localStorage.removeItem('hf-app:auth-store');
        // Clear cookies so middleware doesn't bounce back
        document.cookie = 'hf-app:access-token=; path=/; max-age=0';
        document.cookie = 'hf-app:api-mode=; path=/; max-age=0';
        document.cookie = 'hf-app:onboarding-complete=; path=/; max-age=0';
        window.location.href = '/login';
      }
      throw new ApiClientError(401, 'Unauthorized', {
        message: 'Session expired',
      });
    }

    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = await response.text();
    }

    // H7 — Stale X-Workspace-Id header: when a 403/404 is caused by the
    // workspace header pointing to a workspace the user no longer has access
    // to (deleted, removed, reseeded), clear the stored id and bounce to the
    // workspace picker. Only runs in production mode with an x-workspace-id
    // header present, and only if the response body hints at "workspace" /
    // "tenant" as the cause (so we don't clear on unrelated 403/404).
    if (
      (response.status === 403 || response.status === 404) &&
      headers['x-workspace-id'] &&
      typeof window !== 'undefined' &&
      !isLoggingOut
    ) {
      const bodyMessage =
        body && typeof body === 'object' && 'message' in body
          ? Array.isArray((body as { message: unknown }).message)
            ? ((body as { message: string[] }).message).join('. ')
            : String((body as { message: unknown }).message)
          : typeof body === 'string'
            ? body
            : '';
      const workspaceCause =
        (response.status === 403 && /workspace|tenant|access denied/i.test(bodyMessage)) ||
        (response.status === 404 && /workspace not found/i.test(bodyMessage));

      if (workspaceCause) {
        localStorage.removeItem('hf-app:workspace-id');
        // Also clear the zustand-persist workspace store so getWorkspaceId()
        // doesn't resurrect the stale id on the next request.
        try {
          const raw = localStorage.getItem('hf-app:workspace');
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed?.state) {
              parsed.state.activeWorkspaceId = null;
              localStorage.setItem('hf-app:workspace', JSON.stringify(parsed));
            }
          }
        } catch {
          // ignore malformed JSON
        }
        // Notify the app — WorkspaceGate / a toast listener can react.
        window.dispatchEvent(
          new CustomEvent('hf-app:workspace-invalid', {
            detail: { status: response.status, message: bodyMessage },
          }),
        );
        // Bounce to root so WorkspaceGate redirects to the picker.
        window.location.href = '/';
        throw new ApiClientError(response.status, response.statusText, body);
      }
    }

    throw new ApiClientError(response.status, response.statusText, body);
  }

  // Handle empty responses (204 No Content, etc.)
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    return undefined as T;
  }

  return response.json();
}

function buildUrl(endpoint: string): string {
  const cleanEndpoint = endpoint.replace(/^\/api\//, '/');
  return `${API_CONFIG.baseUrl}${API_CONFIG.versionPrefix}${cleanEndpoint}`;
}

export function getAccessToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(SESSION_KEYS.REAL_TOKEN_KEY);
}

export function getWorkspaceId(): string | null {
  if (typeof window === 'undefined') return null;

  // Primary key set by auth-store.login() + workspace-store.setActiveWorkspace().
  const direct = localStorage.getItem(SESSION_KEYS.REAL_WORKSPACE_KEY);
  if (direct) return direct;
  // Fallback: zustand workspace persist store (race-condition guard for
  // TeamSwitcher pre-hydration / OAuth callback that skips workspace fetch).
  try {
    const raw = localStorage.getItem('hf-app:workspace');
    if (raw) {
      const parsed = JSON.parse(raw);
      const id = parsed?.state?.activeWorkspaceId;
      if (id && id !== 'all') {
        // Sync back to plain key for future reads
        localStorage.setItem(SESSION_KEYS.REAL_WORKSPACE_KEY, id);
        return id;
      }
    }
  } catch {
    // ignore malformed JSON
  }
  return null;
}

// Helper to build query strings from filter objects
export function buildQueryString(params?: Record<string, unknown>): string {
  if (!params) return '';
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(v => searchParams.append(key, String(v)));
      } else {
        searchParams.set(key, String(value));
      }
    }
  }
  const qs = searchParams.toString();
  return qs ? `?${qs}` : '';
}
